import {ArtfactKind, SystemEntity} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import S3 from 'aws-sdk/clients/s3';
import {ReadStream} from 'node:fs';
import mime from 'mime-types';
import {from, Observable, Observer, of} from 'rxjs';
import {concatMap, map} from 'rxjs/operators';
import {v4 as uuid} from 'uuid';

@HookClass('aws.s3')
export class AwsS3Hook extends BaseHook {

    @Hook('2ac9a89e-3c6a-4b51-8eb3-3a09ea4793f2')
    public upload(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.settings
            .pipe(concatMap((settings) => {

                const s3bucket = this.buildS3Bucket(settings);

                return this.system
                    .pipe(concatMap((system: SystemEntity) => {

                        const today = new Date();
                        let itemId = uuid();

                        if (inputArtifact.object.id)
                            itemId = inputArtifact.object.id;

                        let bucketName: string;
                        const bucket = `/system/${system.id}/${today.getFullYear()}/${today.getMonth()}/${today.getDate()}`;
                        let extension = mime.extension(inputArtifact.object.contentType);
                        if (extension === false && inputArtifact.object.contentType == 'application/x-zip-compressed')
                            extension = 'zip';
                        let key = `${itemId}.${extension}`;

                        if (inputArtifact.object.config && inputArtifact.object.config.bucket)
                            bucketName = inputArtifact.object.config.bucket;

                        if (inputArtifact.object.config && inputArtifact.object.config.key)
                            key = inputArtifact.object.config.key;

                        const alreadyBuffer = inputArtifact.object.buffer instanceof Buffer
                            || inputArtifact.object.buffer instanceof ReadStream;

                        const params: any = {
                            Bucket: `${bucketName ? bucketName : settings.aws.bucketName}${bucketName ? '' : '/' + settings.aws.bucketPrefix}${bucketName ? '' : bucket}`,
                            ContentType: inputArtifact.object.contentType,
                            Key: key,
                            Body: alreadyBuffer ? inputArtifact.object.buffer : Buffer.from(inputArtifact.object.buffer, 'binary')
                        };

                        if (inputArtifact.object.config && inputArtifact.object.config.publicRead)
                            params.ACL = 'public-read';

                        return new Observable((observer: Observer<any>) => {
                            try {

                                s3bucket.upload(params, (err: any, data: any) => {
                                    if (err) {
                                        observer.error(err);
                                    } else {
                                        const output = new NickProcessArtfact();
                                        output.kind = ArtfactKind.ANY;
                                        output.object = {
                                            data: {
                                                id: itemId,
                                                fileName: params.Key,
                                                key: data.Key
                                            }
                                        };

                                        if (inputArtifact.object.config && inputArtifact.object.config.publicRead)
                                            output.object.data.publicFile = data.Location;

                                        observer.next(output);
                                    }
                                    observer.complete();
                                });
                            } catch (e) {
                                console.log(e);
                            }

                        });
                    }));
            }));
    }

    @Hook('53997a50-b6e2-4a74-b4f2-25f167ea9526')
    public delete(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.settings
            .pipe(concatMap((settings) => {
                const s3bucket = this.buildS3Bucket(settings);

                let key = inputArtifact.object?.key
                    ? inputArtifact.object.config.key.split('.').reduce((o: any, i: any) => (o ? o[i] : o), inputArtifact.object)
                    : inputArtifact.object;

                const ignoreEnv = inputArtifact.object.config?.ignoreEnv;

                if (!ignoreEnv && key.indexOf(`develop/`) == -1 && key.indexOf(`production/`) == -1)
                    key = `${settings.aws.bucketPrefix}/${key}`;

                const options = {
                    Bucket: inputArtifact.object.config && inputArtifact.object.config.bucket
                        ? inputArtifact.object.config.bucket
                        : settings.aws.bucketName,
                    Delete: {
                        Objects: [
                            {
                                Key: key
                            }
                        ]
                    }
                };

                return from(s3bucket.deleteObjects(options).promise())
                    .pipe(concatMap((data: any) => {
                        const output: any = new NickProcessArtfact();
                        output.kind = ArtfactKind.DOWNLOAD;
                        output.object = data;
                        return of(output);
                    }));
            }));
    }

    @Hook('c74e4e92-7273-4a83-82ce-3dc25907435d')
    public getUploadInfo(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.settings
            .pipe(concatMap((settings) => {
                return this.system
                    .pipe(map((system: SystemEntity) => {

                        const today = new Date();
                        const itemId = uuid();

                        let bucketName: string;
                        const bucket = `/system/${system.id}/${today.getFullYear()}/${today.getMonth()}/${today.getDate()}`;
                        let extension = mime.extension(inputArtifact.object.contentType);
                        if (extension === false && inputArtifact.object.contentType == 'application/x-zip-compressed')
                            extension = 'zip';
                        let key = `${itemId}.${extension}`;

                        if (inputArtifact.object.config && inputArtifact.object.config.bucket)
                            bucketName = inputArtifact.object.config.bucket;

                        if (inputArtifact.object.config && inputArtifact.object.config.key)
                            key = inputArtifact.object.config.key;

                        const alreadyBuffer = inputArtifact.object.buffer instanceof Buffer
                            || inputArtifact.object.buffer instanceof ReadStream;

                        const params: any = {
                            Bucket: `${bucketName ? bucketName : settings.aws.bucketName}${bucketName ? '' : '/' + settings.aws.bucketPrefix}${bucketName ? '' : bucket}`,
                            ContentType: inputArtifact.object.contentType,
                            Key: key
                        };

                        if (inputArtifact.object.config && inputArtifact.object.config.publicRead)
                            params.ACL = 'public-read';

                        const output: any = new NickProcessArtfact();
                        output.kind = ArtfactKind.ANY;
                        output.object = {
                            params,
                            credentials: {
                                params: {Bucket: settings.aws.bucketName},
                                apiVersion: '2006-03-01',
                                region: 'us-east-1',
                                credentials: {accessKeyId: settings.aws.accessKeyId, secretAccessKey: settings.aws.secretAccessKey}
                            }

                        };
                        return output;

                    }));
            }));
    }

    @Hook('e824dc3c-0b01-4e1a-a754-69ae0025ca55')
    public download(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.settings
            .pipe(concatMap((settings) => {
                const s3bucket = this.buildS3Bucket(settings);

                const options = {
                    Bucket: inputArtifact.object.config && inputArtifact.object.config.bucket
                        ? inputArtifact.object.config.bucket
                        : settings.aws.bucketName,
                    Key: inputArtifact.object.config && inputArtifact.object.config.key
                        ? inputArtifact.object.config.key.split('.').reduce((o: any, i: any) => (o ? o[i] : o), inputArtifact.object)
                        : inputArtifact.object.data.key
                };

                const output = new NickProcessArtfact();
                output.kind = ArtfactKind.DOWNLOAD;
                output.object = s3bucket.getObject(options).createReadStream();

                return of(output);
            }));
    }

    @Hook('5baf3595-1514-4890-9f8f-4ff267bee4c6')
    public downloadToBuffer(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.settings
            .pipe(concatMap((settings) => {
                const s3bucket = this.buildS3Bucket(settings);

                const options = {
                    Bucket: inputArtifact.object.config && inputArtifact.object.config.bucket
                        ? inputArtifact.object.config.bucket
                        : settings.aws.bucketName,
                    Key: inputArtifact.object.config && inputArtifact.object.config.key
                        ? inputArtifact.object.config.key.split('.').reduce((o: any, i: any) => (o ? o[i] : o), inputArtifact.object)
                        : inputArtifact.object.data.key
                };

                return from(s3bucket.getObject(options).promise())
                    .pipe(concatMap((data: any) => {
                        const output = new NickProcessArtfact();
                        output.kind = ArtfactKind.DOWNLOAD;
                        output.object = data.Body;

                        switch (inputArtifact.object.config?.output) {
                            case 'string':
                                output.object = data.Body.toString();
                                break;
                            case 'base64':
                                output.object = data.Body.toString('base64');
                                break;
                            case 'file': {
                                if (inputArtifact.object.config.fileName)
                                    this.context.response.attachment(inputArtifact.object.config.fileName);

                                this.context.response.type(data.ContentType);

                                output.kind = ArtfactKind.ANY;
                                output.object = new NickProcessArtfact();
                                output.object.kind = 10;
                                output.object.object = data.Body;
                                break;
                            }
                        }

                        return of(output);
                    }));
            }));
    }

    @Hook('8fa98cd4-d743-4931-8df9-2ca961df3e9a')
    public downloadBase64(inputArtifact: NickProcessArtfact): any {
        return this.settings
            .pipe(concatMap((settings) => {
                const s3bucket = this.buildS3Bucket(settings);

                const options = {
                    Bucket: inputArtifact.object.config && inputArtifact.object.config.bucket
                        ? inputArtifact.object.config.bucket
                        : settings.aws.bucketName,
                    Key: inputArtifact.object.config && inputArtifact.object.config.key
                        ? inputArtifact.object.config.key.split('.').reduce((o: any, i: any) => (o ? o[i] : o), inputArtifact.object)
                        : inputArtifact.object.data.key
                };

                const ignoreEnv = inputArtifact.object.config && inputArtifact.object.config.ignoreEnv;

                if (!ignoreEnv && options.Key.indexOf(`develop/`) == -1 && options.Key.indexOf(`production/`) == -1)
                    options.Key = `${settings.aws.bucketPrefix}/${options.Key}`;

                return from(s3bucket.getObject(options).promise())
                    .pipe(concatMap((data: any) => {
                        const output: any = new NickProcessArtfact();
                        output.kind = ArtfactKind.DOWNLOAD;
                        output.key = options.Key;
                        output.object = data.Body.toString('base64');
                        return of(output);
                    }));
            }));
    }

    @Hook('35fb1c27-5848-46e2-bc42-8b7792235866')
    public downloadJson(inputArtifact: NickProcessArtfact): any {
        return this.settings
            .pipe(concatMap((settings) => {
                const s3bucket = this.buildS3Bucket(settings);

                const options = {
                    Bucket: inputArtifact.object.config && inputArtifact.object.config.bucket
                        ? inputArtifact.object.config.bucket
                        : settings.aws.bucketName,
                    Key: inputArtifact.object.config && inputArtifact.object.config.key
                        ? inputArtifact.object.config.key.split('.').reduce((o: any, i: any) => (o ? o[i] : o), inputArtifact.object)
                        : inputArtifact.object.data.key
                };

                const ignoreEnv = inputArtifact.object.config && inputArtifact.object.config.ignoreEnv;

                if (!ignoreEnv && options.Key.indexOf(`develop/`) == -1 && options.Key.indexOf(`production/`) == -1)
                    options.Key = `${settings.aws.bucketPrefix}/${options.Key}`;

                return from(s3bucket.getObject(options).promise())
                    .pipe(concatMap((data: any) => {
                        const output: any = new NickProcessArtfact();
                        output.kind = ArtfactKind.DOWNLOAD;
                        output.key = options.Key;
                        output.object = JSON.parse(data.Body);
                        return of(output);
                    }));
            }));
    }

    @Hook('90fe5318-0773-418f-9233-044c9cb2913d')
    public link(inputArtifact: NickProcessArtfact): any {
        return this.settings
            .pipe(concatMap((settings) => {
                const s3bucket = this.buildS3Bucket(settings);

                const options = {
                    Bucket: settings.aws.bucketName,
                    Key: inputArtifact.object.key,
                    Expires: 950400
                };

                return of(s3bucket.getSignedUrl('getObject', options))
                    .pipe(concatMap((url: any) => {
                        const output = new NickProcessArtfact();
                        output.kind = ArtfactKind.ANY;
                        output.object = url;
                        return of(output);
                    }));
            }));
    }

    private buildS3Bucket(settings: any): S3 {
        return new S3({
            params: {Bucket: settings.aws.bucketName},
            apiVersion: '2006-03-01',
            region: 'us-east-1',
            credentials: {accessKeyId: settings.aws.accessKeyId, secretAccessKey: settings.aws.secretAccessKey}
        });
    }

}
